/**
 * m.8 Okružnice - čitanje po djelatniku (PIN), ispit uz okružnicu, izvještaj po djelatniku,
 * tjedni pregled "Okružnice ukratko" + tjedni kviz. Backend: CircularLearningController.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { formatDate } from '../../shared/formatting';

type Locale = 'hr' | 'en';

export type EmployeeRead = {
    employee_id: number;
    name: string;
    read_at: string | null;
    source: string;
    quiz: { attempts: number; passed: boolean; last_passed: boolean; failed_attempts: number; last_score: string; last_at: string } | null;
};

type QuizQuestion = { question: string; options: string[]; correct_index?: number; explanation?: string; excerpt?: string; circular_id?: number | null };

const box: React.CSSProperties = { marginTop: 18, padding: '12px 14px', border: '1px solid rgba(var(--ad-soft-rgb),0.45)', borderRadius: 8, background: '#fffdf8' };
const h4: React.CSSProperties = { margin: '0 0 8px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' };
const smallBtn: React.CSSProperties = { background: 'var(--ad-ink)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' };
const ghostBtn: React.CSSProperties = { background: '#fff', color: 'var(--ad-ink)', border: '1px solid rgba(var(--ad-soft-rgb),0.6)', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' };

function quizBadge(q: EmployeeRead['quiz'], t: (hr: string, en: string) => string) {
    if (!q) return null;
    const ok = q.passed;
    return (
        <span title={t(`Pokušaja: ${q.attempts}, netočnih: ${q.failed_attempts}, zadnji ${q.last_score}`, `Attempts: ${q.attempts}, failed: ${q.failed_attempts}, last ${q.last_score}`)}
            style={{ marginLeft: 6, padding: '0 6px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, background: ok ? 'rgba(31,122,54,0.14)' : 'rgba(177,64,64,0.14)', color: ok ? '#1f7a36' : '#b14040' }}>
            {ok ? t('ispit ✓', 'quiz ✓') : t('ispit ✗', 'quiz ✗')}{q.failed_attempts > 0 && ok ? ` (${q.failed_attempts}× ${t('netočno', 'wrong')})` : ''}
        </span>
    );
}

/** Ispod adrese primatelja: koji su djelatnici pročitali (PIN) i rezultat ispita. */
export function EmployeeReadsList({ reads, locale }: { reads?: EmployeeRead[]; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (!reads || reads.length === 0) return null;
    return (
        <div style={{ marginTop: 3, fontSize: '0.74rem', color: 'var(--ad-ink)', lineHeight: 1.6 }}>
            {reads.map((r) => (
                <div key={r.employee_id}>
                    <span style={{ color: '#1f7a36' }}>✓</span> {r.name} <span className="muted">{r.read_at ? formatDate(r.read_at, locale) : ''}{r.source === 'link' ? ` · ${t('osobni link', 'personal link')}` : ''}</span>
                    {quizBadge(r.quiz, t)}
                </div>
            ))}
        </div>
    );
}

type QuizShow = {
    quiz: { status: 'draft' | 'active' | 'off'; source: string; ai_model: string | null; generated_at: string | null; activated_at: string | null; questions: QuizQuestion[] } | null;
    can_edit: boolean;
    ai_available: boolean;
    results: Array<{ employee_id: number; name: string; attempts: number; passed: boolean; failed_attempts: number; last_score: string; last_at: string }>;
    summary: { employees: number; passed: number; failed_now: number };
};

/** Ispit uz okružnicu: generiraj (Claude), uredi, aktiviraj; rezultati po djelatniku. */
export function CircularQuizPanel({ circularId, token, locale }: { circularId: number; token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<QuizShow | null>(null);
    const [hidden, setHidden] = useState(false);
    const [edit, setEdit] = useState<QuizQuestion[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [count, setCount] = useState(2);

    const load = useCallback(async () => {
        try {
            const d = await api<QuizShow>(`/circulars/${circularId}/quiz`, token);
            setData(d);
            setHidden(false);
        } catch {
            setHidden(true);
        }
    }, [circularId, token]);
    useEffect(() => { setEdit(null); setMsg(null); void load(); }, [load]);

    if (hidden || !data) return null;
    const q = data.quiz;

    const generate = async () => {
        if (q && q.status === 'active' && !confirm(t('Ispit je aktivan. Novi prijedlog ga vraća u nacrt dok ga ponovno ne aktivirate. Nastaviti?', 'Quiz is active. A new draft deactivates it until re-activated. Continue?'))) return;
        setBusy(true); setMsg(t('Claude sastavlja pitanja…', 'Claude is drafting questions…'));
        try {
            const r = await mutate<{ questions: QuizQuestion[] }>(`/circulars/${circularId}/quiz/generate`, token, 'POST', { count });
            setEdit(r.questions);
            setMsg(t('Prijedlog je spreman. Provjerite pitanja i točne odgovore pa aktivirajte.', 'Draft ready. Check questions and correct answers, then activate.'));
            await load();
        } catch (e) {
            setMsg(t('AI nije uspio: ', 'AI failed: ') + (e instanceof Error ? e.message : ''));
        } finally { setBusy(false); }
    };
    const save = async (status: 'draft' | 'active' | 'off', questions: QuizQuestion[]) => {
        setBusy(true); setMsg(null);
        try {
            await mutate(`/circulars/${circularId}/quiz`, token, 'PUT', { status, questions });
            setEdit(null);
            setMsg(status === 'active' ? t('Ispit je aktivan. Djelatnici ga dobivaju nakon PIN-a.', 'Quiz active. Employees get it after PIN.') : status === 'off' ? t('Ispit je isključen.', 'Quiz disabled.') : t('Nacrt spremljen.', 'Draft saved.'));
            await load();
        } catch (e) {
            setMsg(e instanceof Error ? e.message : 'Greška');
        } finally { setBusy(false); }
    };
    const upd = (i: number, patch: Partial<QuizQuestion>) => setEdit((cur) => (cur ?? []).map((x, j) => (j === i ? { ...x, ...patch } : x)));

    const statusLbl = !q ? t('nema ispita', 'no quiz') : q.status === 'active' ? t('aktivan', 'active') : q.status === 'off' ? t('isključen', 'disabled') : t('nacrt', 'draft');

    return (
        <div style={box} data-testid="circular-quiz-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h4 style={{ ...h4, margin: 0 }}>{t('Provjera razumijevanja (ispit)', 'Comprehension quiz')} · {statusLbl}{q ? ` · ${q.questions.length} ${t('pitanja', 'questions')}` : ''}</h4>
                {data.can_edit && !edit ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select value={count} onChange={(e) => setCount(Number(e.target.value))} disabled={busy} style={{ fontSize: '0.78rem' }} aria-label={t('Broj pitanja', 'Question count')}>
                            {[1, 2, 3].map((n) => <option key={n} value={n}>{n} {n === 1 ? t('pitanje', 'question') : t('pitanja', 'questions')}</option>)}
                        </select>
                        <button type="button" style={smallBtn} disabled={busy || !data.ai_available} onClick={() => void generate()} title={data.ai_available ? '' : t('Claude API ključ nije postavljen', 'Claude API key missing')}>
                            ✨ {q ? t('Novi prijedlog (Claude)', 'New draft (Claude)') : t('Generiraj ispit (Claude)', 'Generate quiz (Claude)')}
                        </button>
                        {q ? <button type="button" style={ghostBtn} disabled={busy} onClick={() => setEdit(q.questions)}>✏️ {t('Uredi', 'Edit')}</button> : <button type="button" style={ghostBtn} disabled={busy} onClick={() => setEdit([{ question: '', options: ['', '', ''], correct_index: 0, explanation: '', excerpt: '' }])}>✏️ {t('Ručno', 'Manual')}</button>}
                        {q && q.status !== 'active' && q.questions.length > 0 ? <button type="button" style={{ ...smallBtn, background: '#1f7a36' }} disabled={busy} onClick={() => void save('active', q.questions)}>▶ {t('Aktiviraj', 'Activate')}</button> : null}
                        {q && q.status === 'active' ? <button type="button" style={ghostBtn} disabled={busy} onClick={() => void save('off', q.questions)}>⏸ {t('Isključi', 'Disable')}</button> : null}
                    </div>
                ) : null}
            </div>
            <p className="muted" style={{ fontSize: '0.76rem', margin: '6px 0 0' }}>
                {t('Djelatnik dobiva pitanja nakon što se identificira PIN-om. Rezultat se bilježi odvojeno od čitanja; netočan odgovor vraća ga na označeni dio upute.', 'Employees get the questions after PIN identification. Result is stored separately from reading; a wrong answer sends them back to the marked part of the instruction.')}
            </p>
            {msg ? <div style={{ marginTop: 6, fontSize: '0.8rem', fontWeight: 600 }}>{msg}</div> : null}

            {edit ? (
                <div style={{ marginTop: 10 }}>
                    {edit.map((qq, i) => (
                        <div key={i} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 6, padding: 8, marginBottom: 8, background: '#fff' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <strong>{i + 1}.</strong>
                                <textarea value={qq.question} onChange={(e) => upd(i, { question: e.target.value })} rows={2} style={{ flex: 1, fontSize: '0.82rem' }} aria-label={t('Pitanje', 'Question')} />
                                <button type="button" style={ghostBtn} onClick={() => setEdit((cur) => (cur ?? []).filter((_, j) => j !== i))}>✕</button>
                            </div>
                            {qq.options.map((o, j) => (
                                <label key={j} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0 0 18px', fontSize: '0.8rem' }}>
                                    <input type="radio" name={`ci-${i}`} checked={qq.correct_index === j} onChange={() => upd(i, { correct_index: j })} title={t('Točan odgovor', 'Correct answer')} />
                                    <input value={o} onChange={(e) => upd(i, { options: qq.options.map((x, k) => (k === j ? e.target.value : x)) })} style={{ flex: 1, fontSize: '0.8rem' }} />
                                </label>
                            ))}
                            <div style={{ margin: '4px 0 0 18px', display: 'flex', gap: 6 }}>
                                {qq.options.length < 4 ? <button type="button" style={ghostBtn} onClick={() => upd(i, { options: [...qq.options, ''] })}>+ {t('odgovor', 'option')}</button> : null}
                                {qq.options.length > 2 ? <button type="button" style={ghostBtn} onClick={() => upd(i, { options: qq.options.slice(0, -1), correct_index: Math.min(qq.correct_index ?? 0, qq.options.length - 2) })}>− {t('odgovor', 'option')}</button> : null}
                            </div>
                            <label style={{ display: 'block', margin: '6px 0 0 18px', fontSize: '0.76rem' }}>{t('Odlomak upute (kamo vraća netočan odgovor)', 'Instruction excerpt (where a wrong answer points)')}
                                <textarea value={qq.excerpt ?? ''} onChange={(e) => upd(i, { excerpt: e.target.value })} rows={2} style={{ width: '100%', fontSize: '0.78rem' }} />
                            </label>
                        </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {edit.length < 3 ? <button type="button" style={ghostBtn} onClick={() => setEdit((cur) => [...(cur ?? []), { question: '', options: ['', '', ''], correct_index: 0, explanation: '', excerpt: '' }])}>+ {t('pitanje', 'question')}</button> : null}
                        <button type="button" style={ghostBtn} disabled={busy} onClick={() => void save('draft', edit)}>💾 {t('Spremi nacrt', 'Save draft')}</button>
                        <button type="button" style={{ ...smallBtn, background: '#1f7a36' }} disabled={busy || edit.length === 0} onClick={() => void save('active', edit)}>▶ {t('Spremi i aktiviraj', 'Save and activate')}</button>
                        <button type="button" style={ghostBtn} disabled={busy} onClick={() => setEdit(null)}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : q && q.questions.length > 0 ? (
                <ol style={{ margin: '8px 0 0 18px', padding: 0, fontSize: '0.8rem' }}>
                    {q.questions.map((qq, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                            {qq.question}
                            <div className="muted" style={{ fontSize: '0.76rem' }}>
                                {qq.options.map((o, j) => <span key={j} style={{ marginRight: 10, fontWeight: qq.correct_index === j ? 700 : 400, color: qq.correct_index === j ? '#1f7a36' : undefined }}>{String.fromCharCode(65 + j)}) {o}</span>)}
                            </div>
                        </li>
                    ))}
                </ol>
            ) : null}

            {data.results.length > 0 ? (
                <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700 }}>
                        {t('Rezultati po djelatniku', 'Results per employee')}: {data.summary.passed}/{data.summary.employees} {t('položilo', 'passed')}{data.summary.failed_now > 0 ? <span style={{ color: '#b14040' }}> · {data.summary.failed_now} {t('još nije položilo', 'not passed yet')}</span> : null}
                    </div>
                    <table className="admin-user-table" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                        <thead><tr><th>{t('Djelatnik', 'Employee')}</th><th>{t('Rezultat', 'Result')}</th><th>{t('Pokušaja', 'Attempts')}</th><th>{t('Netočnih', 'Wrong')}</th><th>{t('Zadnji', 'Last')}</th></tr></thead>
                        <tbody>
                            {data.results.map((r) => (
                                <tr key={r.employee_id}>
                                    <td>{r.name}</td>
                                    <td style={{ fontWeight: 700, color: r.passed ? '#1f7a36' : '#b14040' }}>{r.passed ? t('položen', 'passed') : t('nije položen', 'not passed')} ({r.last_score})</td>
                                    <td>{r.attempts}</td>
                                    <td>{r.failed_attempts}</td>
                                    <td>{r.last_at ? formatDate(r.last_at, locale) : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}

type EmpReport = {
    employees: Array<{ employee_id: number; name: string; read: number; quiz_passed: number; quiz_failed: number; quiz_pending: number; items: Array<{ circular_id: number; label: string; read_at: string; via: string | null; source: string; quiz: { passed: boolean; attempts: number; failed_attempts: number; last_score: string } | null; quiz_required: boolean }> }>;
    weekly: Array<{ digest_id: number; period_to: string; employee_id: number; name: string; attempts: number; passed: boolean; best: number; total: number; last_at: string }>;
};

type PinReq = { id: number; first_name: string; last_name: string; employee_id: number | null; outcome: string; pin_shown: boolean; notify_status: string; notified_emails: string[]; recipient_email: string | null; requested_at: string; department: string | null };

/** Zahtjevi za PIN sa stranica okružnica: samoprijave i postojeći djelatnici (voditelj dobiva mail). */
export function PinRequestsList({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<PinReq[] | null>(null);
    useEffect(() => { api<{ rows: PinReq[] }>('/circulars/pin-requests', token).then((d) => setRows(d.rows)).catch(() => setRows([])); }, [token]);
    if (!rows || rows.length === 0) return null;
    const lbl: Record<string, string> = {
        created: t('novi djelatnik, PIN prikazan', 'new employee, PIN shown'),
        existing_pin: t('postoji, PIN poslan voditelju', 'exists, PIN sent to manager'),
        existing_new_pin: t('postoji bez PIN-a, novi PIN voditelju', 'existed without PIN, new PIN to manager'),
        ambiguous: t('više istih imena, provjeriti', 'ambiguous name, check'),
    };
    return (
        <div data-testid="pin-requests">
            <h4 style={{ ...h4, marginTop: 14 }}>{t('Zahtjevi za PIN', 'PIN requests')} ({rows.length})</h4>
            <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                <thead><tr><th>{t('Vrijeme', 'Time')}</th><th>{t('Upisano ime', 'Name entered')}</th><th>{t('Poslovnica', 'Branch')}</th><th>{t('Ishod', 'Outcome')}</th><th>{t('Voditelju', 'Manager')}</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} style={r.outcome === 'created' || r.outcome === 'ambiguous' ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                            <td>{formatDate(r.requested_at, locale)}</td>
                            <td>{r.first_name} {r.last_name}</td>
                            <td>{r.department ?? r.recipient_email ?? '-'}</td>
                            <td>{lbl[r.outcome] ?? r.outcome}</td>
                            <td className="muted">{r.notify_status === 'sent' ? r.notified_emails.join(', ') : r.notify_status === 'no_manager' ? t('voditelj nije pronađen', 'manager not found') : t('slanje nije uspjelo', 'sending failed')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Izvještaj po djelatniku (Tajana t.1-3): čitanje, ispit, tjedni kviz. */
export function EmployeeReportView({ token, locale, dateFrom }: { token: string; locale: Locale; dateFrom: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<EmpReport | null>(null);
    const [open, setOpen] = useState<number | null>(null);
    const [err, setErr] = useState<string | null>(null);
    useEffect(() => {
        setData(null); setErr(null);
        api<EmpReport>(`/circulars/employee-report${dateFrom ? `?date_from=${dateFrom}` : ''}`, token).then(setData).catch((e) => setErr(e instanceof Error ? e.message : 'Greška'));
    }, [token, dateFrom]);
    if (err) return <p className="error">{err}</p>;
    if (!data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (data.employees.length === 0 && data.weekly.length === 0) return <><p className="muted">{t('Još nema čitanja uz PIN.', 'No PIN reads yet.')}</p><PinRequestsList token={token} locale={locale} /></>;
    return (
        <div data-testid="employee-report">
            <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                <thead><tr><th>{t('Djelatnik', 'Employee')}</th><th>{t('Pročitao', 'Read')}</th><th>{t('Ispit položen', 'Quiz passed')}</th><th>{t('Ispit nije položen', 'Quiz failed')}</th><th>{t('Ispit nije rješavan', 'Quiz not taken')}</th></tr></thead>
                <tbody>
                    {data.employees.map((e) => (
                        <React.Fragment key={e.employee_id}>
                            <tr onClick={() => setOpen(open === e.employee_id ? null : e.employee_id)} style={{ cursor: 'pointer' }}>
                                <td><strong>{open === e.employee_id ? '▾' : '▸'} {e.name}</strong></td>
                                <td>{e.read}</td>
                                <td style={{ color: '#1f7a36' }}>{e.quiz_passed}</td>
                                <td style={{ color: e.quiz_failed ? '#b14040' : undefined, fontWeight: e.quiz_failed ? 700 : 400 }}>{e.quiz_failed}</td>
                                <td>{e.quiz_pending}</td>
                            </tr>
                            {open === e.employee_id ? e.items.map((it) => (
                                <tr key={`${e.employee_id}-${it.circular_id}`} style={{ background: 'rgba(var(--ad-acc-rgb),0.05)' }}>
                                    <td style={{ paddingLeft: 24 }}>{it.label}</td>
                                    <td>{formatDate(it.read_at, locale)}</td>
                                    <td colSpan={3} className="muted">{it.via ?? ''}{it.quiz ? ` · ${it.quiz.passed ? t('položen', 'passed') : t('nije položen', 'not passed')} (${it.quiz.last_score}, ${it.quiz.attempts}×)` : it.quiz_required ? ` · ${t('ispit nije rješavan', 'quiz not taken')}` : ''}</td>
                                </tr>
                            )) : null}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            {data.weekly.length > 0 ? (
                <>
                    <h4 style={{ ...h4, marginTop: 14 }}>{t('Tjedni kviz', 'Weekly quiz')}</h4>
                    <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                        <thead><tr><th>{t('Tjedan do', 'Week to')}</th><th>{t('Djelatnik', 'Employee')}</th><th>{t('Položen', 'Passed')}</th><th>{t('Najbolje', 'Best')}</th><th>{t('Pokušaja', 'Attempts')}</th></tr></thead>
                        <tbody>
                            {data.weekly.map((w) => (
                                <tr key={`${w.digest_id}-${w.employee_id}`}>
                                    <td>{formatDate(w.period_to, locale)}</td><td>{w.name}</td>
                                    <td style={{ color: w.passed ? '#1f7a36' : '#b14040', fontWeight: 700 }}>{w.passed ? t('da', 'yes') : t('ne', 'no')}</td>
                                    <td>{w.best}/{w.total}</td><td>{w.attempts}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            ) : null}
            <PinRequestsList token={token} locale={locale} />
        </div>
    );
}

type WeeklyRow = { id: number; period_from: string; period_to: string; status: string; recipient_count: number; sent_count: number; sent_at: string | null; ai_model: string | null; circular_count: number; question_count: number; employees_answered: number };
type WeeklyIndex = { rows: WeeklyRow[]; settings: { enabled: boolean; extra_emails: string; is_production: boolean; schedule: string }; can_manage: boolean };
type WeeklyDetail = {
    digest: { id: number; period_from: string; period_to: string; status: string; summary_html: string | null; ai_model: string | null; failure_note: string | null; sent_at: string | null; recipient_count: number; sent_count: number; questions: QuizQuestion[]; circulars: Array<{ id: number; subject: string; circular_number: string | null }> };
    recipients: Array<{ email_address: string; status: string; sent_at: string | null; opened_at: string | null }>;
    planned_recipients: string[];
    results: Array<{ employee_id: number; name: string; attempts: number; passed: boolean; failed_attempts: number; last_score: string }>;
};

/** Tjedni pregled "Okružnice ukratko" petkom u 14h + tjedni kviz. */
export function WeeklyDigestModal({ token, locale, onClose }: { token: string; locale: Locale; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [idx, setIdx] = useState<WeeklyIndex | null>(null);
    const [detail, setDetail] = useState<WeeklyDetail | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [extra, setExtra] = useState('');

    const load = useCallback(async () => {
        const d = await api<WeeklyIndex>('/circulars/weekly', token);
        setIdx(d); setExtra(d.settings.extra_emails);
    }, [token]);
    useEffect(() => { void load().catch((e) => setMsg(e instanceof Error ? e.message : 'Greška')); }, [load]);
    const openDetail = async (id: number) => setDetail(await api<WeeklyDetail>(`/circulars/weekly/${id}`, token));

    const generate = async () => {
        setBusy(true); setMsg(t('Sastavljam pregled zadnjeg tjedna (Claude)…', 'Building last week summary (Claude)…'));
        try {
            const r = await mutate<{ id: number; status: string }>('/circulars/weekly/generate', token, 'POST', {});
            setMsg(r.status === 'empty' ? t('U zadnjem tjednu nema okružnica.', 'No circulars last week.') : t('Nacrt je spreman.', 'Draft ready.'));
            await load(); await openDetail(r.id);
        } catch (e) { setMsg(e instanceof Error ? e.message : 'Greška'); } finally { setBusy(false); }
    };
    const send = async (id: number) => {
        if (!confirm(t('Poslati pregled svim adresama koje su taj tjedan primile okružnicu?', 'Send the summary to all addresses that received a circular that week?'))) return;
        setBusy(true);
        try {
            const r = await mutate<{ sent: number; failed: number; recipients: number }>(`/circulars/weekly/${id}/send`, token, 'POST', {});
            setMsg(t(`Poslano ${r.sent}, neuspjelo ${r.failed}.`, `Sent ${r.sent}, failed ${r.failed}.`));
            await load(); await openDetail(id);
        } catch (e) { setMsg(e instanceof Error ? e.message : 'Greška'); } finally { setBusy(false); }
    };
    const saveSettings = async (patch: { enabled?: boolean; extra_emails?: string }) => {
        setBusy(true);
        try { await mutate('/circulars/weekly-settings', token, 'PATCH', patch); await load(); setMsg(t('Postavke spremljene.', 'Settings saved.')); }
        catch (e) { setMsg(e instanceof Error ? e.message : 'Greška'); } finally { setBusy(false); }
    };

    return (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', padding: 24 }} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 'min(980px, 100%)', padding: 18 }} data-testid="weekly-digest-modal">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <strong>🗓 {t('Tjedni pregled: Okružnice ukratko + tjedni kviz', 'Weekly: Circulars in brief + weekly quiz')}</strong>
                    <button type="button" style={ghostBtn} onClick={onClose}>✕</button>
                </div>
                {idx ? (
                    <div style={{ ...box, marginTop: 10 }}>
                        <div style={{ fontSize: '0.82rem' }}>
                            {t('Raspored', 'Schedule')}: <strong>{t('petak 14:00', 'Friday 14:00')}</strong> · {t('automatsko slanje', 'automatic sending')}: <strong style={{ color: idx.settings.enabled ? '#1f7a36' : '#b14040' }}>{idx.settings.enabled ? t('uključeno', 'on') : t('isključeno', 'off')}</strong>
                            {!idx.settings.is_production ? <span className="muted"> · {t('ovaj stroj ne šalje mail (slanje nije uključeno ovdje)', 'this server does not send (sending not enabled here)')}</span> : null}
                        </div>
                        {idx.can_manage ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                                <button type="button" style={idx.settings.enabled ? ghostBtn : { ...smallBtn, background: '#1f7a36' }} disabled={busy} onClick={() => void saveSettings({ enabled: !idx.settings.enabled })}>
                                    {idx.settings.enabled ? t('Isključi automatsko slanje', 'Turn off automatic sending') : t('Uključi automatsko slanje', 'Turn on automatic sending')}
                                </button>
                                <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder={t('dodatne adrese voditelja, zarezom (samo @aurodomus)', 'extra manager addresses, comma separated (@aurodomus only)')} style={{ flex: 1, minWidth: 260, fontSize: '0.8rem' }} />
                                <button type="button" style={ghostBtn} disabled={busy} onClick={() => void saveSettings({ extra_emails: extra })}>💾</button>
                                <button type="button" style={smallBtn} disabled={busy} onClick={() => void generate()}>✨ {t('Sastavi pregled zadnjeg tjedna', 'Build last week summary')}</button>
                            </div>
                        ) : null}
                        {msg ? <div style={{ marginTop: 6, fontSize: '0.8rem', fontWeight: 600 }}>{msg}</div> : null}
                    </div>
                ) : <p className="muted">{msg ?? t('Učitavam…', 'Loading…')}</p>}

                {idx && idx.rows.length > 0 ? (
                    <table className="admin-user-table" style={{ fontSize: '0.8rem', marginTop: 12 }}>
                        <thead><tr><th>{t('Razdoblje', 'Period')}</th><th>{t('Status', 'Status')}</th><th>{t('Okružnica', 'Circulars')}</th><th>{t('Pitanja', 'Questions')}</th><th>{t('Poslano', 'Sent')}</th><th>{t('Riješilo djelatnika', 'Employees answered')}</th></tr></thead>
                        <tbody>
                            {idx.rows.map((r) => (
                                <tr key={r.id} onClick={() => void openDetail(r.id)} style={{ cursor: 'pointer', background: detail?.digest.id === r.id ? 'rgba(var(--ad-acc-rgb),0.08)' : undefined }}>
                                    <td>{formatDate(r.period_from, locale)} - {formatDate(r.period_to, locale)}</td>
                                    <td>{r.status === 'sent' ? t('poslano', 'sent') : r.status === 'empty' ? t('prazno', 'empty') : r.status === 'failed' ? t('neuspjelo', 'failed') : t('nacrt', 'draft')}</td>
                                    <td>{r.circular_count}</td><td>{r.question_count}</td>
                                    <td>{r.sent_count}/{r.recipient_count}</td><td>{r.employees_answered}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : null}

                {detail ? (
                    <div style={{ ...box }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <strong>{t('Okružnice ukratko', 'Circulars in brief')} {formatDate(detail.digest.period_from, locale)} - {formatDate(detail.digest.period_to, locale)}</strong>
                            {idx?.can_manage && detail.digest.status === 'draft' ? <button type="button" style={smallBtn} disabled={busy || !idx.settings.is_production} onClick={() => void send(detail.digest.id)}>📤 {t(`Pošalji sada (${detail.planned_recipients.length} adresa)`, `Send now (${detail.planned_recipients.length} addresses)`)}</button> : null}
                        </div>
                        {detail.digest.failure_note ? <p className="muted" style={{ fontSize: '0.76rem' }}>{detail.digest.failure_note}</p> : null}
                        <div style={{ fontSize: '0.84rem' }} dangerouslySetInnerHTML={{ __html: detail.digest.summary_html ?? '' }} />
                        {detail.digest.questions.length > 0 ? (
                            <>
                                <h4 style={{ ...h4, marginTop: 10 }}>{t('Tjedni kviz', 'Weekly quiz')} ({detail.digest.questions.length})</h4>
                                <ol style={{ margin: '0 0 0 18px', padding: 0, fontSize: '0.8rem' }}>
                                    {detail.digest.questions.map((q, i) => <li key={i}>{q.question} <span className="muted">{q.correct_index !== undefined ? `(${t('točno', 'correct')}: ${q.options[q.correct_index]})` : ''}</span></li>)}
                                </ol>
                            </>
                        ) : null}
                        {detail.results.length > 0 ? (
                            <>
                                <h4 style={{ ...h4, marginTop: 10 }}>{t('Rezultati po djelatniku', 'Results per employee')}</h4>
                                <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                                    <thead><tr><th>{t('Djelatnik', 'Employee')}</th><th>{t('Rezultat', 'Result')}</th><th>{t('Pokušaja', 'Attempts')}</th></tr></thead>
                                    <tbody>{detail.results.map((r) => <tr key={r.employee_id}><td>{r.name}</td><td style={{ color: r.passed ? '#1f7a36' : '#b14040', fontWeight: 700 }}>{r.passed ? t('položen', 'passed') : t('nije položen', 'not passed')} ({r.last_score})</td><td>{r.attempts}</td></tr>)}</tbody>
                                </table>
                            </>
                        ) : null}
                        <p className="muted" style={{ fontSize: '0.76rem', marginTop: 8 }}>
                            {detail.digest.status === 'sent'
                                ? t(`Poslano ${detail.digest.sent_count} adresa, otvoreno ${detail.recipients.filter((r) => r.opened_at).length}.`, `Sent to ${detail.digest.sent_count}, opened ${detail.recipients.filter((r) => r.opened_at).length}.`)
                                : t(`Planirani primatelji: ${detail.planned_recipients.join(', ')}`, `Planned recipients: ${detail.planned_recipients.join(', ')}`)}
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
